import { useCallback, useRef, useMemo } from "react";
import { CloudSearchConfig } from "../../components/shared/CloudSearchConfig";
import { OpenSearchConfig } from '../../components/shared/OpenSearchConfig';
import DATA_MODEL_TABLE from "../constants/dataModelTable";
import { isEqual } from "underscore";
import { listOfFieldsIgnoredForSearch } from "../../constants";
import { transformLuceneValues, SEARCH_ENGINE_TYPES } from "@digitallab/grid-common-components";
import { getFieldValue, OPENSEARCH_CONFIGURATION } from "../../features/booking-instruments/helpers";

const CLOUDSEARCH_FIELD_KEY_MAPPINGS = {
  category: "equipmentCategory",
};
// Equipment status constants
const equipmentStatus = {
  active: { value: "Active", key: "active" },
  pending: { value: "Pending", key: "pending" },
  deleted: { value: "Deleted", key: "deleted" },
};

// Entry type constants
const entryType = {
  cluster: "CLUSTER",
  standaloneEquipment: "STANDALONE-EQUIPMENT",
  clusterSubequipment: "CLUSTER-SUB-EQUIPMENT",
};

// Helper functions implemented locally (from EquipmentList.tsx logic)
const convertToSnakeCase = (str) => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

const convertToCameCase = (str) => {
  return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
};

const replaceSpecialCharWithSpace = (str) => {
  if (typeof str !== "string") return str;
  // eslint-disable-next-line
  return str.replace(/[!@#$%^&*()+=\[\]{};:\\|,<>?~\-"\/]/g, " ").trim();
};

const checkNullInArrayOfStr = (list = []) => {
  if (!Array.isArray(list)) return false;
  return list.some(
    (item) =>
      item === null ||
      item === undefined ||
      item === "" ||
      item === "null" ||
      (typeof item === "string" && item.trim() === ""),
  );
};

const searchApi = (searchParams) => {
  return OPENSEARCH_CONFIGURATION ? OpenSearchConfig(searchParams) : CloudSearchConfig(searchParams);
}

const getFilterModelQuery = ({
  filterModel,
  type,
  filterName = "",
  masterDatas = {},
  metric = "metric",
  literalFields = [],
}) => {
  if (!filterModel || Object.keys(filterModel).length === 0) return "";

  const queries = [];

  Object.keys(filterModel).forEach((key) => {
    const filterObj = filterModel[key];

    if (type === "set" && filterObj.values) {
      if (filterObj.values.length === 0) return;

      // Handle null values
      const hasNull = checkNullInArrayOfStr(filterObj.values);
      const cleanValues = filterObj.values.filter((v) => v !== null && v !== undefined && v !== "" && v !== "null");

      let valueQuery = "";
      if (cleanValues.length > 0) {
        valueQuery = cleanValues.map((v) => `${key}:"${v}"`).join(" OR ");
      }

      if (hasNull) {
        const nullQuery = `(NOT ${key}:*)`;
        valueQuery = valueQuery ? `(${valueQuery} OR ${nullQuery})` : nullQuery;
      } else if (valueQuery) {
        valueQuery = `(${valueQuery})`;
      }

      if (valueQuery) {
        queries.push(valueQuery);
      }
    } else if ((type === "text" || type === "literal") && filterObj.filter) {
      const textValue = replaceSpecialCharWithSpace(filterObj.filter);
      const isWildcard = filterObj.type !== "equals";
      const transformed = transformLuceneValues({
        value: textValue,
        type: type === "text" ? SEARCH_ENGINE_TYPES.TEXT : SEARCH_ENGINE_TYPES.LITERAL,
        escapeSpecialChars: true,
        isWildcard,
      });
      if (filterObj.type === "contains") {
        queries.push(`${key}:*${transformed}*`);
      } else if (filterObj.type === "equals") {
        queries.push(`${key}:${transformed}`);
      } else if (filterObj.type === "startsWith") {
        queries.push(`${key}:${transformed}*`);
      } else if (filterObj.type === "endsWith") {
        queries.push(`${key}:*${transformed}`);
      }
    } else if (type === "number") {
      const { filter, filterTo, type: numberType } = filterObj;

      if (numberType === "equals" && filter !== undefined) {
        queries.push(`${key}:${filter}`);
      } else if (numberType === "greaterThan" && filter !== undefined) {
        queries.push(`${key}:{${filter} TO *}`);
      } else if (numberType === "lessThan" && filter !== undefined) {
        queries.push(`${key}:{* TO ${filter}}`);
      } else if (numberType === "inRange" && filter !== undefined && filterTo !== undefined) {
        queries.push(`${key}:{${filter} TO ${filterTo}}`);
      }
    } else if (type === "date") {
      const { dateFrom, dateTo, type: dateType } = filterObj;

      if (dateType === "equals" && dateFrom) {
        const date = new Date(dateFrom).toISOString().split("T")[0];
        queries.push(`${key}:"${date}"`);
      } else if (dateType === "greaterThan" && dateFrom) {
        const date = new Date(dateFrom).toISOString().split("T")[0];
        queries.push(`${key}:{${date} TO *}`);
      } else if (dateType === "lessThan" && dateFrom) {
        const date = new Date(dateFrom).toISOString().split("T")[0];
        queries.push(`${key}:{* TO ${date}}`);
      } else if (dateType === "inRange" && dateFrom && dateTo) {
        const fromDate = new Date(dateFrom).toISOString().split("T")[0];
        const toDate = new Date(dateTo).toISOString().split("T")[0];
        queries.push(`${key}:{${fromDate} TO ${toDate}}`);
      }
    }
  });

  return queries.length > 0 ? queries.join(" AND ") : "";
};

/**
 * Helper function to check if cached data is stale
 */
const isCacheStale = (timestamp, maxAgeMs = 60 * 1000) => {
  return Date.now() - timestamp > maxAgeMs;
};

/**
 * Helper function to wait for cache to be populated by another request
 */
const waitForCache = (cacheRef, cacheKey, signal) => {
  return new Promise((resolve) => {
    const checkCache = () => {
      if (signal?.aborted) {
        return resolve(null);
      }

      if (cacheRef.current.has(cacheKey)) {
        const cached = cacheRef.current.get(cacheKey);
        const isStale = isCacheStale(cached.timestamp, 1 * 1000);
        if (!isStale) {
          return resolve({
            items: cached.data,
            cached: true,
            cacheKey,
          });
        }
      }
      setTimeout(checkCache, 100);
    };
    checkCache();
  });
};

/**
 * Helper function to transform cloud search results to expected format
 */
const transformCloudSearchResults = (data) => {
  const toSnake = (s) => s.replace(/([A-Z])/g, "_$1").toLowerCase();

  return data
    .filter((item) => {
      // Filter out cluster sub-equipment, keep only standalone and cluster main entries
      const entryTypeValue = item.entry_type && getFieldValue(item, 'entry_type');
      return entryTypeValue === "STANDALONE-EQUIPMENT" || entryTypeValue === "CLUSTER";
    })
    .map((item) => {
      const obj = {
        inventoryId: getFieldValue(item, 'id'),
        equipmentId: getFieldValue(item, 'equipment_id'),
        equipmentModel: getFieldValue(item, 'equipment_model'),
        equipmentNickName: getFieldValue(item, 'equipment_nick_name'),
        serialNumber: getFieldValue(item, 'serial_number'),
        status: getFieldValue(item, 'status'),
        siteName: getFieldValue(item, 'site_name'),
        isBookable: getFieldValue(item, 'is_bookable') === "true",
        entryType: getFieldValue(item, 'entry_type'),
        locationKey: getFieldValue(item, 'location'),
        clusterId: getFieldValue(item, 'cluster_id'),
        location: {
          building: getFieldValue(item, 'building_location'),
          floor: getFieldValue(item, 'floor'),
          room: getFieldValue(item, 'room'),
        },
      };
      Object.values(DATA_MODEL_TABLE).forEach(({ key }) => {
        if (obj[key] !== undefined) return;
        const src = toSnake(key);
        obj[key] = getFieldValue(item, src);
      });
      return obj;
    });
};

const sanitizeFilterDefinition = (filterDef) => {
  const FILTER_KEYS_TO_OMIT = [...listOfFieldsIgnoredForSearch, "group"];
  return Object.keys(filterDef).reduce((acc, key) => {
    if (FILTER_KEYS_TO_OMIT.includes(key)) {
      return acc;
    }
    const fieldWithKey = filterDef[key];
    const newKey = CLOUDSEARCH_FIELD_KEY_MAPPINGS[key];
    const fieldWithNewKey = newKey ? filterDef[newKey] : undefined;
    if (newKey && fieldWithNewKey !== undefined && isEqual(fieldWithNewKey, fieldWithKey)) {
      acc[newKey] = fieldWithKey;
    } else {
      acc[key] = fieldWithKey;
    }
    return acc;
  }, {});
};

/**
 * Helper function to convert filter definition to cloud search query
 * Based on the exact logic from EquipmentList.tsx getRows function
 */
const convertFilterToCloudSearch = (filterDefinition, siteName, masterDataFilters = {}) => {
  // Convert filterDefinition to agGridFilterModel format
  let agGridFilterModel = {};
  let listFilterObject = {};
  let textFilterObject = {};
  let numberFilterObject = {};
  let dateFilterObject = {};
  filterDefinition = sanitizeFilterDefinition(filterDefinition);
  if (filterDefinition && Object.keys(filterDefinition).length > 0) {
    Object.entries(filterDefinition).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        return;
      }

      // Skip inventoryIds as it's handled separately for MY_LAB filter
      if (key === "inventoryIds") {
        return;
      }

      const snakeKey = convertToSnakeCase(key);
      if (Array.isArray(value) && value.length > 0) {
        // Set filter
        agGridFilterModel[snakeKey] = {
          values: value,
          filterType: "set",
        };
      } else if (typeof value === "object" && value.filterType) {
        // Complex filter object
        agGridFilterModel[snakeKey] = value;
      } else if (typeof value === "string") {
        // Text filter
        agGridFilterModel[snakeKey] = {
          filterType: "text",
          type: "contains",
          filter: value,
        };
      }
    });
  }

  // Process agGridFilterModel exactly like EquipmentList.tsx
  Object.keys(agGridFilterModel).forEach((key) => {
    let filterObj = agGridFilterModel[key];
    const cameCaseKey = convertToCameCase(key);

    if (filterObj?.filterType === "set" && Object.keys(masterDataFilters).includes(cameCaseKey)) {
      if (
        checkNullInArrayOfStr(filterObj?.values) ||
        filterObj?.values?.length === 0 ||
        checkNullInArrayOfStr(filterDefinition?.[key])
      ) {
        if (!checkNullInArrayOfStr(filterObj?.values)) {
          filterObj?.values?.push(null);
        }
      }
      listFilterObject[key] = filterObj;
    } else if (filterObj?.filterType === "date") {
      dateFilterObject[key] = filterObj;
    } else if (filterObj?.filterType === "number") {
      numberFilterObject[key] = filterObj;
    } else if (filterObj?.filterType === "text") {
      textFilterObject[key] = filterObj;
    } else {
      listFilterObject[key] = filterObj;
    }
  });

  // Handle MY_LAB filter with inventoryIds
  if (
    filterDefinition?.inventoryIds &&
    Array.isArray(filterDefinition.inventoryIds) &&
    filterDefinition.inventoryIds.length > 0
  ) {
    listFilterObject = {
      ...listFilterObject,
      _id: { values: filterDefinition.inventoryIds },
    };
  }

  let filterQuery = null;
  if (
    Object.keys(listFilterObject)?.length === 0 &&
    Object.keys(dateFilterObject)?.length === 0 &&
    Object.keys(textFilterObject)?.length === 0 &&
    Object.keys(numberFilterObject)?.length === 0
  ) {
    const equipmentStatuses = `(or status:'${equipmentStatus?.active?.value}' status:'${equipmentStatus?.pending?.value}')`;
    const equipmentRelations = `(or entry_type:'${entryType?.cluster}' entry_type:'${entryType?.standaloneEquipment}')`;
    const siteNameFilter = siteName ? `(or site_name:'${siteName}')` : ``;
    const bookableFilter = `(or is_bookable:'true')`;

    filterQuery = `(and ${equipmentStatuses} ${equipmentRelations} ${siteNameFilter ? ` ${siteNameFilter}` : ``} ${bookableFilter})`;
  }

  // Build searchTxtQuery exactly like EquipmentList.tsx
  let searchTxtQuery = "";

  if (Object.keys(listFilterObject)?.length !== 0) {
    searchTxtQuery = getFilterModelQuery({
      filterModel: listFilterObject,
      type: "set",
      filterName: filterDefinition?.filterName || "",
      masterDatas: masterDataFilters,
      literalFields: [],
    });
  }

  if (Object.keys(textFilterObject)?.length > 0) {
    const strAnd = searchTxtQuery?.length > 0 ? ` AND ` : ` `;
    searchTxtQuery =
      searchTxtQuery +
      strAnd +
      getFilterModelQuery({
        filterModel: textFilterObject,
        type: "text",
      });
  }

  if (Object.keys(numberFilterObject)?.length > 0) {
    const strAnd = searchTxtQuery?.length > 0 ? ` AND ` : ` `;
    searchTxtQuery =
      searchTxtQuery +
      strAnd +
      getFilterModelQuery({
        filterModel: numberFilterObject,
        type: "number",
        metric: "metric",
      });
  }

  if (Object.keys(dateFilterObject)?.length > 0) {
    const strAnd = searchTxtQuery?.length > 0 ? ` AND ` : ` `;
    searchTxtQuery =
      searchTxtQuery +
      strAnd +
      getFilterModelQuery({
        filterModel: dateFilterObject,
        type: "date",
      });
  }

  // Add site and bookable restrictions like EquipmentList.tsx
  if (siteName && searchTxtQuery) {
    searchTxtQuery = `${searchTxtQuery} AND (site_name:"${siteName}")`;
  }

  if (searchTxtQuery) {
    searchTxtQuery = `${searchTxtQuery} AND (is_bookable:"true")`;
  }

  // Final search value - use base query if nothing specific
  let searchValue = searchTxtQuery;
  if (!searchValue || searchValue.trim() === "") {
    searchValue = "* AND -cluster_id:*";
  }

  return { searchValue, filterQuery };
};

/**
 * Custom hook for caching instrument queries using cloud search
 * Provides intelligent caching and request deduplication for cloud search equipment queries
 */
export const useInstrumentCache = ({ client, user, masterDataFilters = {} }) => {
  const cacheRef = useRef(new Map());
  const loadingRef = useRef(new Set());

  // Generate cache key based on filter and site
  const generateCacheKey = useCallback((siteName, filter) => {
    const sortedFilter = Object.keys(filter)
      .sort()
      .reduce((acc, key) => {
        acc[key] = filter[key];
        return acc;
      }, {});
    return `${siteName}-${JSON.stringify(sortedFilter)}`;
  }, []);

  /**
   * Helper function to check and retrieve from cache
   */
  const getCachedData = useCallback((cacheKey) => {
    if (!cacheRef.current.has(cacheKey)) {
      return null;
    }

    const cached = cacheRef.current.get(cacheKey);
    const isStale = isCacheStale(cached.timestamp, 1 * 60 * 1000);

    if (!isStale) {
      return {
        items: cached.data,
        cached: true,
        cacheKey,
      };
    }

    // Remove stale cache
    cacheRef.current.delete(cacheKey);
    return null;
  }, []);

  /**
   * Helper function to fetch and transform data from search API
   */
  const fetchInstrumentData = useCallback(
    async (siteName, filterDefinition, localMasterDataFilters, signal) => {
      const { searchValue, filterQuery } = convertFilterToCloudSearch(filterDefinition, siteName, localMasterDataFilters);
      const serialNumberField = convertToSnakeCase(DATA_MODEL_TABLE.serialNumber.key) + (OPENSEARCH_CONFIGURATION ? ".keyword" : "");

      const cloudSearchParams = {
        start: 0,
        size: 1000,
        searchValue,
        sort: "asc",
        sortBy: serialNumberField,
        filterQuery,
        signal,
      };

      const result = await searchApi(cloudSearchParams);

      if (!result || !result.data) {
        console.warn("EquipmentBookingGrid:useInstrumentCache: No data returned from cloud search");
        return [];
      }

      return transformCloudSearchResults(result.data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [masterDataFilters],
  );

  // Memoized instrument loader with cloud search and intelligent caching
  const loadInstruments = useCallback(
    async (siteName, filterDefinition = {}, options = {}) => {
      const { signal } = options;
      const cacheKey = generateCacheKey(siteName, filterDefinition);
      // Check if request was aborted before starting
      if (signal?.aborted) {
        return;
      }

      // Check if already loading this exact request
      if (loadingRef.current.has(cacheKey)) {
        return waitForCache(cacheRef, cacheKey, signal);
      }

      // Check cache first
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      // Check abort signal again before proceeding
      if (signal?.aborted) {
        return;
      }

      // Mark as loading
      loadingRef.current.add(cacheKey);

      try {
        // Check abort signal before making request
        if (signal?.aborted) {
          return;
        }

        // Fetch and transform data
        const transformedItems = await fetchInstrumentData(siteName, filterDefinition, masterDataFilters, signal);

        // Final abort check before caching
        if (signal?.aborted) {
          return;
        }

        // Cache the result
        cacheRef.current.set(cacheKey, {
          data: transformedItems,
          timestamp: Date.now(),
        });

        // Limit cache size to prevent memory leaks
        if (cacheRef.current.size > 100) {
          const oldestKey = cacheRef.current.keys().next().value;
          cacheRef.current.delete(oldestKey);
        }

        return {
          items: transformedItems,
          cached: false,
          cacheKey,
        };
      } catch (error) {
        if (error.message === "AbortError" || error.name === "AbortError") {
          throw error;
        }
        console.error("EquipmentBookingGrid:useInstrumentCache: Error executing cloud search:", error);
        return {
          items: [],
          cached: false,
          error,
          cacheKey,
        };
      } finally {
        // Remove from loading set
        loadingRef.current.delete(cacheKey);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generateCacheKey, getCachedData, fetchInstrumentData],
  );

  // Clear cache for specific filter or all
  const clearCache = useCallback(
    (siteName = null, filterDefinition = null) => {
      if (siteName && filterDefinition) {
        const cacheKey = generateCacheKey(siteName, filterDefinition);
        cacheRef.current.delete(cacheKey);
      } else {
        cacheRef.current.clear();
      }
    },
    [generateCacheKey],
  );

  // Get cache statistics
  const getCacheStats = useCallback(() => {
    const entries = Array.from(cacheRef.current.entries());
    const totalEntries = entries.length;
    const totalItems = entries.reduce((sum, [, value]) => sum + value.data.length, 0);
    const avgAge =
      entries.length > 0
        ? entries.reduce((sum, [, value]) => sum + (Date.now() - value.timestamp), 0) / entries.length
        : 0;

    return {
      totalEntries,
      totalItems,
      avgAgeSeconds: Math.round(avgAge / 1000),
      loadingRequests: loadingRef.current.size,
      cacheKeys: entries.map(([key]) => key),
    };
  }, []);

  // Preload instruments for common filters
  const preloadInstruments = useCallback(
    async (siteName, filters = []) => {
      const promises = filters.map((filter) =>
        loadInstruments(siteName, filter).catch((error) => {
          console.warn(
            "EquipmentBookingGrid:useInstrumentCache: Cloud search preload failed for filter",
            filter,
            error,
          );
          return { items: [], error };
        }),
      );

      const results = await Promise.all(promises);

      return results;
    },
    [loadInstruments],
  );

  // Memoize the hook return value to prevent recreations
  return useMemo(
    () => ({
      loadInstruments,
      clearCache,
      getCacheStats,
      preloadInstruments,
      loadingRequestsCount: loadingRef.current.size,
    }),
    [loadInstruments, clearCache, getCacheStats, preloadInstruments],
  );
};

export default useInstrumentCache;
